import {FC, useEffect, useRef, useState} from 'react';

import {useNavigation} from '@remix-run/react';

interface ReadMoreProps {
  copy: string;
  shouldTruncate?: boolean;
  lines?: number;
  isReadMore?: boolean;
  className?: string;
}

export const ReadMore: FC<ReadMoreProps> = ({
  copy,
  shouldTruncate = false,
  lines = 5,
  isReadMore = false,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(!shouldTruncate);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const {state} = useNavigation();
  const elRef = useRef(null);

  // We need to adjust the number of lines to render in non-expanded version for the case when first few lines are
  // pure html. Otherwise, it will be empty, as HTML is not visible.
  const countHtmlLines = (html: string) => {
    const regex = /<[^>]*>/g; // Matches HTML tags
    let match;
    let htmlLineCount = 0;

    while ((match = regex.exec(html)) !== null) {
      if (match.index === regex.lastIndex) {
        regex.lastIndex++;
      }
      // Count lines that consist only of HTML tags
      if (match[0].trim() !== "" && !match[0].includes("\n")) {
        htmlLineCount++;
      }
    }
    return htmlLineCount;
  };

  const additionalLines = countHtmlLines(copy);
  const effectiveLines = lines + additionalLines;

  const truncatedCopy = copy.split('\n').slice(0, effectiveLines).join('\n');


  useEffect(() => {
    setIsExpanded(!shouldTruncate);
  }, [state, shouldTruncate, elRef]);
  useEffect(() => {
    if (elRef.current) {
      setIsOverflowing(isOverflown(elRef?.current));
    } else {
      setIsOverflowing(false);
    }
  }, [elRef]);

  return (
    <div className="relative">
      <div
        ref={elRef}
        style={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: isExpanded ? 'unset' : lines,
          overflow: 'hidden',
          position: 'relative',
        }}
        className={`prose prose-neutral gap-1 mt-1 break-words ${className}`}
        dangerouslySetInnerHTML={{
          __html: isExpanded ? copy : truncatedCopy,
        }}
      />

      {!isExpanded && !isReadMore && (
        <div
          style={{
            position: 'absolute',
            bottom: '25px',
            left: 0,
            right: 0,
            height: '3rem',
            backgroundImage:
              'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))',
          }}
        />
      )}
      {!isExpanded && isOverflowing && (
        <button
          onClick={() => setIsExpanded(true)}
          className="relative text-expertPrimary underline pt-2"
        >
          {isReadMore ? 'Read' : 'See'} More
        </button>
      )}
    </div>
  );
};

const isOverflown = (element: HTMLElement) => {
  return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
};
